import { WritableAtom, atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { SetStateAction } from 'react';

import { HUB_URL } from 'hooks/signalR';

import { debugSignalRAtom } from './logger.atom';
import { LogSignalRMessageValue, LogSignalRState } from './types';

const debugSignalRHubSelector = atomFamily<
  HUB_URL,
  WritableAtom<LogSignalRState[HUB_URL], [SetStateAction<LogSignalRState[HUB_URL]>], void>
>((hub) => {
  const innerAtom = atom(
    (get) => get(debugSignalRAtom)[hub],
    (get, set, newValue) => {
      const state = get(debugSignalRAtom);
      set(debugSignalRAtom, {
        ...state,
        [hub]: newValue,
      });
    },
  );
  innerAtom.debugLabel = 'Debug/SignalRHub Selector';
  return innerAtom;
});

export const debugSignalRHubInitSelector = atomFamily<
  HUB_URL,
  WritableAtom<LogSignalRMessageValue, [SetStateAction<Partial<LogSignalRMessageValue>>], void>
>((hub) => {
  const innerAtom = atom(
    (get) => get(debugSignalRHubSelector(hub)).init,
    (get, set, updater: SetStateAction<Partial<LogSignalRMessageValue>>) => {
      const state = get(debugSignalRHubSelector(hub));
      const newValue = typeof updater === 'function' ? updater(state.init) : updater;

      set(debugSignalRHubSelector(hub), {
        ...(state || {}),
        init: newValue as LogSignalRMessageValue,
      });
    },
  );

  innerAtom.debugLabel = 'Debug/SignalRHub Init Selector';
  return innerAtom;
});

export const debugSignalRHubReceiveSelector = atomFamily<
  HUB_URL,
  WritableAtom<LogSignalRMessageValue, [SetStateAction<Partial<LogSignalRMessageValue>>], void>
>((hub) => {
  const innerAtom = atom(
    (get) => get(debugSignalRHubSelector(hub)).receive,
    (get, set, updater: SetStateAction<Partial<LogSignalRMessageValue>>) => {
      const state = get(debugSignalRHubSelector(hub));
      const receive = typeof updater === 'function' ? updater(state.receive) : updater;

      set(debugSignalRHubSelector(hub), {
        ...(state || {}),
        receive: receive as LogSignalRMessageValue,
      });
    },
  );

  innerAtom.debugLabel = 'Debug/SignalRHub Receive Selector';
  return innerAtom;
});

export const debugSignalRHubInvokeSelector = atomFamily<
  HUB_URL,
  WritableAtom<LogSignalRMessageValue, [SetStateAction<Partial<LogSignalRMessageValue>>], void>
>((hub) => {
  const innerAtom = atom(
    (get) => get(debugSignalRHubSelector(hub)).invoke,
    (get, set, invoke: SetStateAction<Partial<LogSignalRMessageValue>>) => {
      const state = get(debugSignalRHubSelector(hub));
      set(debugSignalRHubSelector(hub), {
        ...(state || {}),
        invoke: invoke as LogSignalRMessageValue,
      });
    },
  );

  innerAtom.debugLabel = 'Debug/SignalRHub Invoke Selector';
  return innerAtom;
});
